import * as React from 'react'

type PageMainProps = React.HTMLAttributes<HTMLElement>

/**
 * Single source of truth for the `<main id="main-content">` landmark every
 * page renders. The skip-link in `__root.tsx` targets this id; routing it
 * through one component keeps the contract un-forgettable as the app grows.
 *
 * Only one PageMain should be rendered per route tree. Nesting them (a
 * parent layout and a child route both rendering PageMain) would produce a
 * duplicate `id="main-content"` and break the skip-link's first-match
 * targeting; in dev we surface that as a loud console warning.
 */
export const PageMain = React.forwardRef<HTMLElement, PageMainProps>(
  function PageMain({ children, ...rest }, ref) {
    React.useEffect(() => {
      if (!import.meta.env?.DEV) return
      const matches = document.querySelectorAll('#main-content')
      if (matches.length > 1) {
        console.warn(
          `[PageMain] ${matches.length} elements with id="main-content" mounted; only one PageMain may render per route tree.`,
        )
      }
    })
    return (
      <main ref={ref} id="main-content" {...rest}>
        {children}
      </main>
    )
  },
)
